import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { CleanupReportGrid } from './CleanupReportGrid';
import {
    ConversionCleanupReportModel,
    getCleanupReport,
} from '../../services/conversionCleanupReportService';

interface ArchivedReportsPageState {
    records: ConversionCleanupReportModel[];
    retentionDays: number;
    isLoading: boolean;
    error: string | null;
    searchText: string;
    conversionTypeFilter: string;
}

class ArchivedReportsPage extends React.Component<RouteComponentProps, ArchivedReportsPageState> {
    constructor(props: RouteComponentProps) {
        super(props);
        this.state = {
            records: [],
            retentionDays: 0,
            isLoading: true,
            error: null,
            searchText: '',
            conversionTypeFilter: '',
        };
    }

    componentDidMount() {
        this.loadReport();
    }

    private loadReport = async () => {
        try {
            this.setState({ isLoading: true, error: null });
            const response = await getCleanupReport();
            this.setState({ records: response.records, retentionDays: response.retentionDays, isLoading: false });
        } catch (err: any) {
            const status = err?.status;
            const message = status === 401 || status === 403
                ? 'Your session has expired. Please log in again to view this report.'
                : 'Failed to load archived status of conversion projects. Please try again.';
            this.setState({ isLoading: false, error: message });
        }
    };

    private handleSearchChange = (value: string) => {
        this.setState({ searchText: value });
    };

    private handleConversionTypeChange = (value: string) => {
        this.setState({ conversionTypeFilter: value });
    };

    private clearFilters = () => {
        this.setState({ searchText: '', conversionTypeFilter: '' });
    };

    private getFilteredRecords(): ConversionCleanupReportModel[] {
        const { records, searchText, conversionTypeFilter } = this.state;
        const normalizedSearch = searchText.trim().toLowerCase();

        return records.filter(record => {
            const typeMatches = !conversionTypeFilter || record.conversionType === conversionTypeFilter;
            const searchMatches = !normalizedSearch || [
                record.conversionName,
                record.conversionType,
                record.runType,
                record.createdByName,
                record.createdByEmail,
                record.deletionException ?? '',
            ].some(value => value?.toLowerCase().includes(normalizedSearch));

            return typeMatches && searchMatches;
        });
    }

    public render() {
        const { records, retentionDays, isLoading, error, searchText, conversionTypeFilter } = this.state;
        const filteredRecords = this.getFilteredRecords();
        const conversionTypes = Array.from(new Set(records.map(record => record.conversionType).filter(Boolean))).sort();

        return (
            <>
                {isLoading && <p>Loading archived status of conversion projects...</p>}
                {error && <Alert color="danger">{error}</Alert>}
                {!isLoading && !error && (
                    <CleanupReportGrid
                        records={filteredRecords}
                        retentionDays={retentionDays}
                        searchText={searchText}
                        conversionTypeFilter={conversionTypeFilter}
                        conversionTypes={conversionTypes}
                        onSearchChange={this.handleSearchChange}
                        onConversionTypeChange={this.handleConversionTypeChange}
                        onClearFilters={this.clearFilters}
                        onRowClick={id => this.props.history.push(`/conversion/${id}/edit`)}
                    />
                )}
            </>
        );
    }
}

export default withRouter(ArchivedReportsPage);
